import { Sheet, SheetContent } from '@workspace/ui/components/sheet'
import { cn } from '@workspace/ui/lib/utils'
import type { ReactNode } from 'react'
import { useState } from 'react'
import type { NavItem, NavSection } from './app-sidebar'
import { AppSidebar } from './app-sidebar'
import { AppTopbar } from './app-topbar'

export interface AppLayoutProps {
  children: ReactNode
  activeNav: string

  // Sidebar config — pass either navItems (compact/flat) or navSections (grouped)
  navItems?: NavItem[]
  navSections?: NavSection[]
  /** Whether sidebar starts collapsed. Defaults to true for compact (navItems), false for sections. */
  defaultCollapsed?: boolean
  /** Whether user can toggle collapse. Defaults to true if navSections provided, false for navItems. */
  collapsible?: boolean
  /** Slot rendered in sidebar header below brand (e.g. nothing, or custom widget) */
  sidebarHeaderSlot?: ReactNode
  /** Primary action pinned above the sidebar footer item. */
  sidebarFooterSlot?: ReactNode
  /** Item pinned to the bottom of the sidebar, above the collapse toggle (e.g. Settings) */
  sidebarFooterItem?: NavItem

  // Topbar config
  topbarLeft?: ReactNode
  topbarRight?: ReactNode
  /** Notifications widget rendered in the topbar before topbarRight */
  topbarNotifications?: ReactNode
  /** URL to the brand logo image */
  logoSrc?: string
  /** Where the brand links to. Omit to leave it inert. */
  homeTo?: string
  /**
   * A panel that sits beside the page (not over it): the page keeps its own scroll and stays usable.
   * It pushes the page from 768px and is the caller's to size; below that it is the caller's to cover the screen.
   */
  sidePanel?: ReactNode
}

export function AppLayout({
  children,
  activeNav,
  navItems,
  navSections,
  defaultCollapsed,
  collapsible,
  sidebarHeaderSlot,
  sidebarFooterSlot,
  sidebarFooterItem,
  topbarLeft,
  topbarRight,
  topbarNotifications,
  logoSrc,
  homeTo,
  sidePanel,
}: AppLayoutProps) {
  // Default collapsed state: compact (navItems) → always collapsed, sections → expanded
  const isCompact = !!navItems && !navSections
  const initialCollapsed = defaultCollapsed ?? isCompact
  const canCollapse = collapsible ?? !isCompact

  const [collapsed, setCollapsed] = useState(initialCollapsed)
  const [mobileOpen, setMobileOpen] = useState(false)

  const sidebarProps = {
    navItems,
    navSections,
    activeNav,
    collapsed,
    onToggle: canCollapse ? () => setCollapsed(!collapsed) : undefined,
    headerSlot: sidebarHeaderSlot,
    footerSlot: sidebarFooterSlot,
    footerItem: sidebarFooterItem,
    logoSrc,
    homeTo,
  }

  return (
    <div className="flex h-screen overflow-hidden bg-background">
      {/* Desktop sidebar */}
      <div className="hidden lg:block">
        <AppSidebar {...sidebarProps} />
      </div>

      {/* Mobile sidebar (Sheet drawer) */}
      <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
        <SheetContent side="left" className="w-80 border-r border-border bg-[#111111] p-0">
          <AppSidebar
            {...sidebarProps}
            collapsed={false}
            onToggle={undefined}
            onNavigate={() => setMobileOpen(false)}
          />
        </SheetContent>
      </Sheet>

      <div
        className={cn(
          'flex min-w-0 flex-1 flex-col transition-all duration-200',
          collapsed ? 'lg:pl-16' : 'lg:pl-80'
        )}
      >
        <AppTopbar
          left={topbarLeft}
          right={topbarRight}
          notifications={topbarNotifications}
          onMenuClick={() => setMobileOpen(true)}
        />
        <div className="flex min-h-0 min-w-0 flex-1">
          <main className="min-w-0 flex-1 overflow-x-hidden overflow-y-auto overscroll-none px-4 py-4 lg:px-8 lg:py-6">
            {children}
          </main>
          {sidePanel}
        </div>
      </div>
    </div>
  )
}
